import type { ApiClient } from "@hakili/api-client";
import { messageOf } from "@hakili/app-shell";
import type {
  CabinetEntryAnswer,
  CabinetFeesAnswer,
  CabinetHistoryAnswer,
  FeeLine,
  HistoryLine,
  ThursdayCapacity,
} from "@hakili/contracts";
import { formatDate, formatMoney, formatQuantity, t } from "@hakili/i18n";
import { useCallback, useEffect, useState } from "react";

/**
 * `round C5-2`, `M4`: **the office screens** , « Honoraires », « Mes
 * rapports », and « Mon cabinet »'s capacity (`hakili-expert.jsx`,
 * `Honoraires`, `MesRapports`, `MonCabinet`), the responsable's alone.
 *
 * « Honoraires »: per vehicle controlled at the cabinet's own figure
 * (`OD-29`), grouped per Thursday, the prevented passes said in words , « à
 * trancher », no figure (decision D). « Mes rapports »: the history with the
 * ninety-day validity following the VEHICLE, each report's state and the
 * verdict as Hakili derived it; since round `C6` (`OD-31`, `OD-33`) the
 * document of record and Hakili's sheet opened, the versions kept, Hakili's
 * request for a correction answered by « Corriger le rapport » (the cabinet's
 * own act, the entry opened again on the desk) or a refusal in words ,
 * « Signaler une erreur » withdrawn. The capacity (`OD-30`): declared per
 * coming Thursday, one figure each, zero allowed, the contractual minimum
 * the default , the drawing's four buttons and a free figure beside them.
 */

const DASH = t("common.valeur.tiret");
const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

export function Fees(props: { readonly api: ApiClient }): React.JSX.Element {
  const { api } = props;
  const [answer, setAnswer] = useState<CabinetFeesAnswer | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  useEffect(() => {
    api
      .request<CabinetFeesAnswer>("/api/v1/cabinet/fees", "get")
      .then(setAnswer)
      .catch((failure: unknown) => {
        setRefusal(messageOf(failure, t("common.error.connexionEchouee")));
      });
  }, [api]);
  if (refusal !== null)
    return (
      <p role="alert" className="px-5 pt-5 text-sm text-rose-600">
        {refusal}
      </p>
    );
  if (answer === null)
    return <p className="px-5 pt-5 text-sm text-gray-500">{t("common.state.chargement")}</p>;
  const total = answer.feeAmount === null ? null : answer.feeAmount * answer.lines.length;
  const byDay = new Map<string, FeeLine[]>();
  for (const line of answer.lines) {
    const key = line.dayLabel ?? DASH;
    byDay.set(key, [...(byDay.get(key) ?? []), line]);
  }
  return (
    <div className="pb-20" data-testid="honoraires">
      <div className="px-5 pb-3 pt-5">
        <h1 className="text-xl font-semibold tracking-tight">{t("expert.honoraires.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("expert.honoraires.sousTitre")}</p>
      </div>
      <div className="mb-4 px-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
            <p className="text-xs text-gray-500">{t("expert.honoraires.parVehicule")}</p>
            <p
              className="mt-0.5 text-xl font-semibold text-indigo-600 tabular-nums"
              data-testid="honoraires-figure"
            >
              {answer.feeAmount === null ? DASH : formatQuantity(answer.feeAmount)}
            </p>
            <p className="mt-0.5 text-xs text-gray-400">{t("expert.honoraires.figureNote")}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
            <p className="text-xs text-gray-500">{t("expert.honoraires.periode")}</p>
            <p className="mt-0.5 text-xl font-semibold tabular-nums" data-testid="honoraires-total">
              {total === null ? DASH : formatQuantity(total)}
            </p>
            <p className="mt-0.5 text-xs text-gray-400">
              {fill(
                answer.lines.length > 1 ? "expert.honoraires.nControles" : "expert.honoraires.nControle",
                {
                  n: String(answer.lines.length),
                },
              )}
            </p>
          </div>
        </div>
      </div>
      {[...byDay.entries()].map(([day, lines]) => (
        <div key={day} className="mb-4 px-5">
          <h2 className="mb-2 text-sm font-semibold">{day}</h2>
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <ul className="divide-y divide-gray-50">
              {lines.map((line) => (
                <li
                  key={line.reportId}
                  className="flex items-center justify-between gap-3 px-4 py-3"
                  data-testid={`honoraires-${line.demandReference}`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-900">{line.vehicleLabel}</span>
                    <span className="block text-xs text-gray-500 tabular-nums">
                      {line.demandReference} · {line.sellerName} · {formatDate(new Date(line.reportedAt))}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-semibold tabular-nums">
                    {line.feeAmount === null ? DASH : formatMoney(line.feeAmount)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
      {answer.lines.length === 0 ? (
        <p className="px-5 text-xs text-gray-400" data-testid="honoraires-aucun">
          {t("expert.honoraires.aucun")}
        </p>
      ) : null}
      <div className="px-5 pt-2">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5">
          <p className="text-xs leading-relaxed text-gray-500" data-testid="honoraires-empeches">
            {answer.prevented > 0
              ? fill(
                  answer.prevented > 1
                    ? "expert.honoraires.empechesATrancher"
                    : "expert.honoraires.empecheATrancher",
                  {
                    n: String(answer.prevented),
                  },
                )
              : t("expert.honoraires.acquis")}
          </p>
        </div>
      </div>
    </div>
  );
}

const VERDICT: ReadonlyMap<string, { key: Parameters<typeof t>[0]; shade: string }> = new Map([
  ["conforme", { key: "expert.rapport.conforme", shade: "bg-emerald-50 text-emerald-700" }],
  ["ecartprix", { key: "expert.rapport.ecartPrix", shade: "bg-amber-50 text-amber-700" }],
  ["nonconforme", { key: "expert.rapport.nonConforme", shade: "bg-rose-50 text-rose-600" }],
]);

/**
 * Round `C6`, `M4`: **a document opened by its owner's listing.** The cabinet's
 * paper is listed under `expertiseReport/{demandId}`, Hakili's sheet under
 * `expertiseSheet/{reportId}`; each answer signs a read URL for this call and
 * the screen opens it , a link is a credential, held by nothing, printed
 * nowhere.
 */
const openDocument = async (
  api: ApiClient,
  ownerType: "expertiseReport" | "expertiseSheet",
  ownerId: string,
  documentId: string,
): Promise<void> => {
  const answer = await api.request<{ documents: readonly { id: string; url: string }[] }>(
    "/api/v1/documents/{ownerType}/{ownerId}",
    "get",
    { params: { ownerType, ownerId } },
  );
  const found = answer.documents.find((one) => one.id === documentId);
  if (found === undefined) throw new Error(t("expert.historique.papierAbsent"));
  window.open(found.url, "_blank", "noopener");
};

export function History(props: {
  readonly api: ApiClient;
  /** The desk, once a correction is opened: the entry is to be taken up there. */
  readonly onCorrectionOpened?: (demandId: string) => void;
}): React.JSX.Element {
  const { api, onCorrectionOpened } = props;
  const [reports, setReports] = useState<readonly HistoryLine[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [refusing, setRefusing] = useState<string | null>(null);
  const [words, setWords] = useState("");
  const [word, setWord] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const read = useCallback(async (): Promise<void> => {
    try {
      const answer = await api.request<CabinetHistoryAnswer>("/api/v1/cabinet/reports", "get");
      setReports(answer.reports);
    } catch (failure) {
      setRefusal(messageOf(failure, t("common.error.connexionEchouee")));
    }
  }, [api]);
  useEffect(() => {
    void read();
  }, [read]);

  const act = (work: () => Promise<void>): void => {
    if (busy) return;
    setBusy(true);
    void (async () => {
      try {
        await work();
      } catch (failure) {
        setWord(messageOf(failure, t("common.error.connexionEchouee")));
      } finally {
        setBusy(false);
      }
    })();
  };

  /** « Corriger le rapport »: the cabinet's own act, the entry opened again on the desk. */
  const correct = (report: HistoryLine): void => {
    act(async () => {
      await api.request<CabinetEntryAnswer>("/api/v1/cabinet/demands/{id}/entry/correction", "post", {
        params: { id: report.demandId },
      });
      setWord(t("expert.historique.correctionOuverteMot"));
      await read();
      onCorrectionOpened?.(report.demandId);
    });
  };

  /** Hakili's request refused, in words. */
  const refuse = (report: HistoryLine): void => {
    act(async () => {
      await api.request<unknown>("/api/v1/cabinet/reports/{id}/correction/refuse", "post", {
        params: { id: report.reportId },
        body: { words },
      });
      setWord(t("expert.historique.refusEnvoye"));
      setRefusing(null);
      setWords("");
      await read();
    });
  };

  if (refusal !== null)
    return (
      <p role="alert" className="px-5 pt-5 text-sm text-rose-600">
        {refusal}
      </p>
    );
  return (
    <div className="pb-20" data-testid="historique">
      <div className="px-5 pb-3 pt-5">
        <h1 className="text-xl font-semibold tracking-tight">{t("expert.historique.titre")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("expert.historique.sousTitre")}</p>
      </div>
      {word === null ? null : (
        <p
          className="mx-5 mb-3 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-xs text-emerald-700"
          data-testid="historique-mot"
        >
          {word}
        </p>
      )}
      <div className="px-5">
        {reports === null ? (
          <p className="text-sm text-gray-500">{t("common.state.chargement")}</p>
        ) : reports.length === 0 ? (
          <p className="text-xs text-gray-400" data-testid="historique-aucun">
            {t("expert.historique.aucun")}
          </p>
        ) : (
          <ul className="space-y-3">
            {reports.map((report) => {
              const verdict = VERDICT.get(report.verdict);
              const ref = report.demandReference;
              return (
                <li
                  key={report.reportId}
                  className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
                  data-testid={`historique-${ref}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{report.vehicleLabel}</p>
                      <p className="text-xs text-gray-500 tabular-nums">
                        {ref} · {report.sellerName}
                        {report.clientName === null ? "" : ` · ${report.clientName}`}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${verdict?.shade ?? "bg-gray-100 text-gray-600"}`}
                    >
                      {verdict === undefined ? report.verdict : t(verdict.key)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500 tabular-nums">
                    {fill("expert.feuille.faitsSur", {
                      n: String(report.points),
                      m: String(report.totalPoints),
                    })}{" "}
                    · {formatMoney(report.estimatedValueAmount)} · {formatDate(new Date(report.reportedAt))}
                  </p>
                  <p className="mt-1 text-xs text-gray-500" data-testid={`historique-${ref}-validite`}>
                    {fill("expert.historique.valableJusquau", {
                      date: formatDate(new Date(`${report.validUntil}T00:00:00Z`)),
                    })}
                    {report.visaByName === null
                      ? ""
                      : ` · ${fill("expert.historique.visePar", { name: report.visaByName })}`}
                  </p>
                  {/* Round `C6` (`OD-33`): the version and the fingerprint wherever the document is opened. */}
                  <p
                    className="mt-1 break-all text-xs text-gray-500 tabular-nums"
                    data-testid={`historique-${ref}-version`}
                  >
                    {fill("expert.historique.version", {
                      n: String(report.version),
                      sha: report.documentSha256 ?? DASH,
                    })}
                    {report.supersededVersions === 0
                      ? ""
                      : ` · ${fill(
                          report.supersededVersions === 1
                            ? "expert.historique.versionsConservees"
                            : "expert.historique.versionsConserveesPluriel",
                          { n: String(report.supersededVersions) },
                        )}`}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={busy || report.documentId === null}
                      title={report.documentId === null ? t("expert.historique.papierAbsent") : undefined}
                      onClick={() => {
                        if (report.documentId === null) return;
                        act(() =>
                          openDocument(api, "expertiseReport", report.demandId, report.documentId ?? ""),
                        );
                      }}
                      data-testid={`historique-${ref}-ouvrir`}
                      className="rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-medium text-indigo-700 disabled:opacity-40"
                    >
                      {t("expert.historique.ouvrirRapport")}
                    </button>
                    <button
                      type="button"
                      disabled={busy || report.derivedSheetDocumentId === null}
                      title={
                        report.derivedSheetDocumentId === null
                          ? t("expert.historique.lectureAbsente")
                          : undefined
                      }
                      onClick={() => {
                        if (report.derivedSheetDocumentId === null) return;
                        act(() =>
                          openDocument(
                            api,
                            "expertiseSheet",
                            report.reportId,
                            report.derivedSheetDocumentId ?? "",
                          ),
                        );
                      }}
                      data-testid={`historique-${ref}-lecture`}
                      className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 disabled:opacity-40"
                    >
                      {t("expert.historique.lectureHakili")}
                    </button>
                  </div>
                  {/* Hakili's request, until the cabinet answers , by a version or a refusal in words. */}
                  {report.correctionRequested === null ? null : (
                    <div
                      className="mt-3 rounded-xl border border-amber-100 bg-amber-50 px-3 py-2.5"
                      data-testid={`historique-${ref}-demande`}
                    >
                      <p className="text-xs leading-relaxed text-amber-700">
                        {fill("expert.historique.correctionDemandee", {
                          date: formatDate(new Date(report.correctionRequested.requestedAt)),
                          reason: report.correctionRequested.reason,
                        })}
                      </p>
                      {refusing === report.reportId ? (
                        <div className="mt-2">
                          <textarea
                            rows={2}
                            value={words}
                            onChange={(event) => {
                              setWords(event.target.value);
                            }}
                            placeholder={t("expert.historique.refuserAide")}
                            data-testid="historique-refus-mots"
                            className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm"
                          />
                          <button
                            type="button"
                            disabled={busy || words.trim() === ""}
                            onClick={() => {
                              refuse(report);
                            }}
                            data-testid="historique-refus-envoyer"
                            className="mt-2 w-full rounded-xl border border-amber-200 bg-white py-2 text-xs font-medium text-amber-700 disabled:opacity-40"
                          >
                            {t("expert.historique.refuserEnvoyer")}
                          </button>
                        </div>
                      ) : report.correctionOpen ? null : (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            setRefusing(report.reportId);
                            setWord(null);
                          }}
                          data-testid={`historique-${ref}-refuser`}
                          className="mt-2 text-xs font-medium text-amber-700"
                        >
                          {t("expert.historique.refuserEnvoyer")}
                        </button>
                      )}
                    </div>
                  )}
                  {/* « Corriger le rapport », the cabinet's own act (`OD-31`), in place of « Signaler une erreur ». */}
                  {report.correctionOpen ? (
                    <p className="mt-2 text-xs text-indigo-700" data-testid={`historique-${ref}-correction`}>
                      {t("expert.historique.correctionOuverte")}
                    </p>
                  ) : (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setWord(null);
                        correct(report);
                      }}
                      data-testid={`historique-${ref}-corriger`}
                      className="mt-2 text-xs font-medium text-indigo-700 disabled:opacity-40"
                    >
                      {t("expert.historique.corrigerRapport")}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

/** One coming Thursday, as `GET /cabinet/me` sends it (the contract expert-web imports). */
export type Thursday = ThursdayCapacity;

/** « Ma capacité du jeudi »: the drawing's four figures, and any other, per coming Thursday. */
export function Capacity(props: {
  readonly thursdays: readonly Thursday[];
  readonly declare: (dayId: string, capacity: number) => Promise<void>;
}): React.JSX.Element {
  const { thursdays, declare } = props;
  const [word, setWord] = useState<string | null>(null);
  const [other, setOther] = useState<Record<string, string>>({});
  const post = (dayId: string, capacity: number): void => {
    void (async () => {
      try {
        await declare(dayId, capacity);
        setWord(fill("expert.capacite.declaree", { n: String(capacity) }));
      } catch (failure) {
        setWord(messageOf(failure, t("common.error.connexionEchouee")));
      }
    })();
  };
  return (
    <>
      <h2 className="mb-2 px-5 text-sm font-semibold">{t("expert.capacite.titre")}</h2>
      <div className="mb-4 px-5">
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4" data-testid="capacite">
          <p className="mb-3 text-xs leading-relaxed text-gray-500">{t("expert.capacite.aide")}</p>
          {word === null ? null : (
            <p
              className="mb-3 rounded-xl bg-indigo-50 px-3 py-2 text-xs text-indigo-700"
              data-testid="capacite-mot"
            >
              {word}
            </p>
          )}
          {thursdays.length === 0 ? (
            <p className="text-xs text-gray-400">{t("expert.capacite.aucunJeudi")}</p>
          ) : (
            <ul className="space-y-3">
              {thursdays.map((thursday) => (
                <li key={thursday.expertiseDayId} data-testid={`capacite-${thursday.dayOn}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900">{thursday.label}</p>
                    <p
                      className="text-xs text-gray-500 tabular-nums"
                      data-testid={`capacite-${thursday.dayOn}-etat`}
                    >
                      {thursday.declared === null
                        ? fill("expert.capacite.minimumParDefaut", { n: String(thursday.minimum) })
                        : fill("expert.capacite.declareeLe", { n: String(thursday.declared) })}
                      {" · "}
                      {fill(
                        thursday.confirmed > 1 ? "expert.capacite.confirmes" : "expert.capacite.confirme",
                        {
                          n: String(thursday.confirmed),
                        },
                      )}
                    </p>
                  </div>
                  {thursday.declared !== null && thursday.declared < thursday.minimum ? (
                    <p
                      className="mt-1 text-xs text-amber-700"
                      data-testid={`capacite-${thursday.dayOn}-sous-minimum`}
                    >
                      {fill("expert.capacite.sousMinimum", { n: String(thursday.minimum) })}
                    </p>
                  ) : null}
                  <div className="mt-2 flex items-center gap-2">
                    {[4, 6, 8, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`capacite-${thursday.dayOn}-${String(n)}`}
                        aria-pressed={thursday.declared === n}
                        onClick={() => {
                          post(thursday.expertiseDayId, n);
                        }}
                        className={`flex-1 rounded-xl border py-2.5 text-sm font-medium tabular-nums ${
                          thursday.declared === n
                            ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                            : "border-gray-200 bg-white text-gray-600"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                    <input
                      inputMode="numeric"
                      value={other[thursday.expertiseDayId] ?? ""}
                      onChange={(event) => {
                        setOther({
                          ...other,
                          [thursday.expertiseDayId]: event.target.value.replace(/\D/gu, ""),
                        });
                      }}
                      placeholder={t("expert.capacite.autre")}
                      data-testid={`capacite-${thursday.dayOn}-autre`}
                      className="w-16 rounded-xl border border-gray-200 px-2 py-2.5 text-center text-sm tabular-nums"
                    />
                    <button
                      type="button"
                      disabled={(other[thursday.expertiseDayId] ?? "") === ""}
                      onClick={() => {
                        post(thursday.expertiseDayId, Number(other[thursday.expertiseDayId]));
                      }}
                      data-testid={`capacite-${thursday.dayOn}-declarer`}
                      className="rounded-xl bg-indigo-600 px-3 py-2.5 text-xs font-medium text-white disabled:opacity-40"
                    >
                      {t("expert.capacite.declarer")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}
